// Iter 83.5 / Phase E4 Sprint 5 — Relieving Letter print.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function RelievingLetterPrint() {
    const { fnfId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data: d } = await api.get(`/print/relieving-letter/${fnfId}`);
                setData(d);
            } catch (err) {
                setError(formatApiError(err, "Failed to load relieving letter"));
                toast.error(formatApiError(err, "Load failed"));
            }
        })();
    }, [fnfId]);

    if (error) return <div className="p-12 text-center text-rose-600" data-testid="relieving-error">{error}</div>;
    if (!data) return <div className="p-12 text-center text-slate-400">Loading…</div>;
    const { fnf, exit_event: exitEvent, employee, company, generated_at } = data;
    const lwd = fnf.lwd || exitEvent?.last_working_day;

    return (
        <div className="bg-white min-h-screen p-14 font-serif" data-testid="relieving-letter-print">
            <style>{`@media print { @page { size: A4; margin: 14mm; } body { -webkit-print-color-adjust: exact; } }`}</style>

            <header className="border-b-4 border-slate-900 pb-4 mb-8 flex justify-between">
                <div>
                    <div className="text-xs uppercase tracking-widest text-slate-600">{company.legal_name}</div>
                    {company.address && <div className="text-[10px] text-slate-500 max-w-xs">{company.address}</div>}
                </div>
                <div className="text-right text-xs">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Date of Issue</div>
                    <div className="font-mono">{(generated_at || "").slice(0, 10)}</div>
                </div>
            </header>

            <h1 className="text-2xl font-bold text-center mb-6 uppercase tracking-widest">Relieving Letter</h1>

            <p className="text-sm mb-3">To,<br />
                <strong>{employee?.full_name}</strong><br />
                Employee Code: {employee?.employee_code}<br />
                {employee?.personal_email || employee?.work_email}
            </p>

            <p className="text-sm mb-4">Dear {employee?.full_name?.split(" ")[0] || "Colleague"},</p>

            <p className="text-sm leading-relaxed mb-4">
                This letter is in reference to your separation from <strong>{company.legal_name}</strong> on
                account of <strong>{fnf.exit_type}</strong>.
            </p>

            <p className="text-sm leading-relaxed mb-4">
                We confirm that you have been relieved from your services with <strong>{company.legal_name}</strong>{" "}
                with effect from end-of-day <strong>{lwd}</strong>. Your last drawn designation was{" "}
                <strong>{employee?.designation || "—"}</strong> and you served in the{" "}
                <strong>{employee?.department || "—"}</strong> department from{" "}
                <strong>{employee?.date_of_joining || "—"}</strong> to <strong>{lwd}</strong>.
            </p>

            <p className="text-sm leading-relaxed mb-4">
                Your Full & Final settlement has been processed and approved. The FNF ID for reference is{" "}
                <span className="font-mono text-xs">{fnf.id}</span>.
                {fnf.is_negative
                    ? " A net recovery is outstanding and is governed by the Outstanding Recoveries lifecycle."
                    : " The net settlement amount has been credited to your registered bank account."}
            </p>

            <p className="text-sm leading-relaxed mb-4">
                You are hereby relieved of all duties and responsibilities at <strong>{company.legal_name}</strong>{" "}
                with effect from <strong>{lwd}</strong>. We confirm that, to the best of our knowledge as on date,
                you have returned all company property and have satisfied the exit checklist.
            </p>

            <p className="text-sm leading-relaxed mb-4">
                Your obligations under the confidentiality, non-solicitation, and intellectual-property
                clauses of your employment agreement remain in force as per their terms.
            </p>

            <p className="text-sm leading-relaxed mb-6">
                We thank you for your contribution and wish you the very best for your future endeavours.
            </p>

            <p className="text-sm mb-12">Yours sincerely,</p>

            <section className="grid grid-cols-2 gap-12">
                <div>
                    <div className="border-t border-slate-700 pt-1 text-xs font-semibold">For {company.legal_name}</div>
                    <div className="text-[10px] text-slate-500 mt-1">Authorised Signatory · Date</div>
                </div>
                <div className="text-[10px] text-slate-500 self-end">
                    <div className="font-semibold uppercase tracking-widest">D28 Reference</div>
                    {fnf.certificate_id && <div className="font-mono mt-0.5">FNF Cert: {fnf.certificate_id}</div>}
                    {fnf.certificate_hash && (
                        <div className="font-mono break-all bg-slate-50 p-1 mt-0.5 rounded text-[9px]">
                            SHA-256: {fnf.certificate_hash}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
